import {captureException} from '@sentry/browser';
import {css} from 'styled-components';

import {HTMLAttributeAnchorTarget, MouseEventHandler, useEffect} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, Nullish, TestIdProps, isURLDangerous} from 'shared/web';

import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {ThemeIconKey} from '../../utils/foundationTheme';
import {HStack} from '../HStack/HStack';
import {Icon} from '../Icon/Icon';
import {Show} from '../Show/Show';
import {Text} from '../Text/Text';

export interface LinkProps extends TestIdProps {
  href?: string | null;
  title?: string | Nullish;
  children?: string;
  size?: 'base' | 'small' | 'xSmall';
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  leftIcon?: ThemeIconKey;
  rightIcon?: ThemeIconKey;
  iconSize?: Integer | ValueByDevice<Integer>;
  /** Use when the link has no visible text (icon-only); sets `aria-label` on the anchor. */
  ariaLabel?: string;
  overflowWrap?: 'anywhere';
  /** Truncates the label to a single line with an ellipsis (requires a width-constrained parent). */
  noWrap?: boolean;
  target?: HTMLAttributeAnchorTarget;
  download?: string | boolean;
  isDisabled?: boolean;
  rel?: string;
}

export function Link(props: LinkProps) {
  useEffect(() => {
    if (isURLDangerous(props.href)) {
      captureException(new Error(`Link component got potentially dangerous URL: ${props.href}`));
      throw new Error('Invalid URL');
    }
  }, [props.href]);

  const hasVisibleLabel = Boolean(props.title || props.children);
  const isIconOnly = !hasVisibleLabel && (isNotNil(props.leftIcon) || isNotNil(props.rightIcon));

  return (
    <a
      role="link"
      rel={props.rel}
      target={props.target}
      download={props.download}
      href={props.isDisabled ? undefined : (props.href ?? undefined)}
      onClick={props.isDisabled ? undefined : props.onClick}
      aria-disabled={props.isDisabled}
      aria-label={isIconOnly ? props.ariaLabel : undefined}
      css={css`
        display: inline-block;
        color: ${({theme}) => theme.colors.general.accent};
        cursor: ${props.isDisabled ? 'not-allowed' : 'pointer'};
        opacity: ${props.isDisabled ? 0.7 : undefined};
        overflow-wrap: ${props.overflowWrap ?? 'initial'};
        word-break: ${props.overflowWrap === 'anywhere' ? 'break-word' : 'initial'};
        max-width: ${props.noWrap || props.overflowWrap === 'anywhere' ? '100%' : 'initial'};
        &:focus {
          box-shadow: none;
        }
        &:hover {
          text-decoration: ${props.isDisabled ? 'none' : 'underline'};
        }
      `}
      data-testid={suffixTestId('link', props)}
    >
      <HStack spacing={1} align="center">
        <Show when={isNotNil(props.leftIcon)}>
          <Icon size={props.iconSize ?? 4} color="general.accent" value={props.leftIcon} />
        </Show>
        <Show when={hasVisibleLabel}>
          <Text
            color="link"
            size={props.size}
            noWrap={props.noWrap}
            overflowWrap={props.overflowWrap}
            title={props.noWrap ? (props.title ?? props.children ?? undefined) : undefined}
            data-testid={suffixTestId('linkContent', props)}
          >
            {props.title || props.children}
          </Text>
        </Show>
        <Show when={isNotNil(props.rightIcon)}>
          <Icon size={props.iconSize ?? 4} color="general.accent" value={props.rightIcon} />
        </Show>
      </HStack>
    </a>
  );
}
